import React from 'react';
import styled from 'styled-components';
import { GridSection, Eyebrow, SectionTitle } from './GridSection';

const Sub = styled.p`
  font-style: italic;
  color: var(--ink-muted);
  font-size: 1rem;
  margin: 0 0 28px;

  a { color: var(--ink-muted); }
  a:hover { color: var(--accent); }
`;

const Item = styled.article`
  padding: 22px 0;
  border-top: 1px solid var(--rule);

  &:last-child { border-bottom: 1px solid var(--rule); }
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px 14px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const Title = styled.a`
  font-family: 'EB Garamond', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--ink);
  text-decoration: none;
  letter-spacing: -0.005em;
  overflow-wrap: anywhere;

  &:hover { color: var(--accent); text-decoration: none; }

  .arrow { color: var(--ink-muted); margin-left: 4px; transition: color .2s; }
  &:hover .arrow { color: var(--accent); }
`;

const Kind = styled.span`
  font-style: italic;
  font-size: 0.95rem;
  color: var(--ink-muted);
`;

const Description = styled.p`
  font-size: 1.04rem;
  color: var(--ink-soft);
  line-height: 1.65;
  margin: 4px 0 8px;
  overflow-wrap: anywhere;
`;

const Stack = styled.div`
  font-style: italic;
  font-size: 0.92rem;
  color: var(--ink-muted);
  overflow-wrap: anywhere;
`;

const Links = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 18px 18px;
  flex-wrap: wrap;
  font-size: 0.95rem;
  font-style: italic;

  a {
    color: var(--ink-soft);
    text-decoration: none;
    border-bottom: 1px solid var(--rule);
    padding: 4px 0 1px;
    transition: color .15s, border-color .15s;
    overflow-wrap: anywhere;
  }
  a:hover { color: var(--accent); border-color: var(--accent); text-decoration: none; }
`;

const projects = [
  {
    title: 'Cross-City SSL Generalization for AV Planning',
    kind: 'master\'s thesis',
    description: 'Self-supervised pretraining on unlabeled driving video to see whether a planning agent trained in one city actually generalizes to another. Built on the NAVSIM benchmark, run on NYU\'s HPC cluster. Early results suggest pretraining helps quite a bit.',
    stack: 'PyTorch, NAVSIM, JEPA, BEV perception, SLURM',
    href: 'https://navsim-showcase.vercel.app/',
    links: [
      { label: 'site ↗', href: 'https://navsim-showcase.vercel.app/' },
      { label: 'thesis source ↗', href: 'https://github.com/hurryingauto3/masters-thesis' },
      { label: 'showcase repo ↗', href: 'https://github.com/hurryingauto3/navsim-showcase' }
    ]
  },
  {
    title: 'AgriYield',
    kind: 'cloud-native MLOps',
    description: 'A production-ish ML platform that predicts county-level crop yields from satellite and weather data. FastAPI on Postgres, with model versioning, schema validation, and drift checks. Most of the actual work was the boring plumbing that makes a model trustworthy in production.',
    stack: 'Python, FastAPI, PostgreSQL, GCP, Vertex AI, Docker',
    href: 'https://github.com/hurryingauto3/AgriYield'
  },
  {
    title: 'RealtorBuddy Core API',
    kind: 'full-stack backend',
    description: 'A Django platform for real-estate agents. Listings, outreach, payments. Multi-tenant on AWS, with the usual caching, queues, and observability bolted on.',
    stack: 'Django, PostgreSQL, Redis, Celery, AWS, Stripe',
    href: 'https://github.com/hurryingauto3/api-django-RealtorBuddyCore'
  },
  {
    title: 'STM32 Tremor Detection',
    kind: 'embedded · real-time',
    description: 'A wearable that tries to detect Parkinson\'s tremors in real time. Bare-metal C with RTOS scheduling, on-device DSP on the accelerometer, and a small classifier that has to fit inside a tight power and latency budget.',
    stack: 'C, STM32, FreeRTOS, DSP',
    href: 'https://github.com/hurryingauto3/rtes-final-project'
  },
  {
    title: 'expflow-hpc',
    kind: 'tooling',
    description: 'A small experiment tracker for SLURM clusters. Submits jobs, sweeps parameters, aggregates artifacts, diffs results. Written because most ML tooling assumes a Kubernetes-shaped world, and HPC isn\'t that.',
    stack: 'Python, SLURM, CLI',
    href: 'https://github.com/hurryingauto3/expflow-hpc'
  },
  {
    title: 'CloudUnify',
    kind: 'systems',
    description: 'Mounts Google Drive, OneDrive, and iCloud as a single filesystem. TypeScript backend with provider adapters and a streaming API. Half the project was the conflict-resolution logic.',
    stack: 'TypeScript, Node.js, FUSE, OAuth',
    href: 'https://github.com/hurryingauto3/cloudunify'
  },
  {
    title: 'SpotifYT',
    kind: 'full-stack',
    description: 'Cross-platform playlist sync between Spotify and YouTube Music. A diffing engine reconciles tracks across providers. Next.js frontend, server-side OAuth.',
    stack: 'Next.js, TypeScript, Spotify API, YouTube API',
    href: 'https://github.com/hurryingauto3/spotifyt'
  },
  {
    title: 'ChatSync',
    kind: 'dev tooling',
    description: 'A VS Code extension that syncs AI chat history across IDEs and machines via Supabase, so context survives editor restarts and the inevitable new laptop.',
    stack: 'TypeScript, VS Code API, Supabase',
    href: 'https://github.com/hurryingauto3/chatsync'
  }
];

function Projects() {
  return (
    <GridSection id="projects">
      <Eyebrow>iii — selected work</Eyebrow>
      <SectionTitle>Things I've built.</SectionTitle>
      <Sub>
        Most of it lives on <a href="https://github.com/hurryingauto3" target="_blank" rel="noopener noreferrer">github.com/hurryingauto3</a>. Each title links out.
      </Sub>
      {projects.map(p => (
        <Item key={p.title}>
          <Head>
            <Title href={p.href} target="_blank" rel="noopener noreferrer">
              {p.title}<span className="arrow"> ↗</span>
            </Title>
            <Kind>— {p.kind}</Kind>
          </Head>
          <Description>{p.description}</Description>
          <Stack>{p.stack}</Stack>
          {p.links && (
            <Links>
              {p.links.map(l => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
              ))}
            </Links>
          )}
        </Item>
      ))}
    </GridSection>
  );
}

export default Projects;
